import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CharacterHeroImage from './CharacterHeroImage';
import bkgImg from '../images/background.webp';
import { makeStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { APICharacterData } from '../types';
import { useRouteMatch } from 'react-router-dom';
import theme from '../theme';
import { SxProps } from '@mui/system';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            position: 'relative',
            width: "100%",
            minHeight: "50vh",
            maxHeight: "50vh",
            backgroundColor: '#666',
            [theme.breakpoints.down('sm')]: {
                minHeight: "75vh",
                maxHeight: "75vh",
            },
        },
        heroImage: {
            position: 'absolute',
            minHeight: '100%',
            maxHeight: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            backgroundImage: `url(${bkgImg})`,
            backgroundPosition: 'bottom',
            backgroundSize: 'cover',
            filter: "grayscale(80%) opacity(60%) contrast(80%) brightness(80%)"
        },
        heroInfo: {
            position: 'absolute',
            minHeight: '100%',
            maxHeight: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            display: 'flex',
            [theme.breakpoints.down('lg')]: {
                display: 'block',
            },
        },
        heroCharImage: {
            width: '40%',
            textAlign: 'right',
            position: "relative",
            zIndex: 10,
            [theme.breakpoints.down('lg')]: {
                width: '100%',
                top: 0,
                left: 0,
                textAlign: 'center',
                height: '60%',
                position: 'absolute',
            },
            [theme.breakpoints.down('sm')]: {
                height: '50%',
            },
        },
        heroCharImageOffset: {
            height: "100%",
            width: "100%",
            position: "relative",
            right: '-25%',
            [theme.breakpoints.down('lg')]: {
                top: 0,
                left: 0,
                right: 0,
            },
        },
        heroCharInfo: {
            width: '60%',
            textAlign: 'left',
            color: "#FFF",
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('lg')]: {
                width: '100%',
            },
        },
        heroCharInfoText: {
            display: 'inline-block',
            textShadow: '1px 1px 5px #000',
            [theme.breakpoints.down('lg')]: {
                textAlign: 'center',
                width: '100%',
                position: 'absolute',
                top: '60%',
                padding: '0 20px',
            },
            [theme.breakpoints.down('sm')]: {
                top: '50%',
            },
        },
        heroInfoError: {
            height: '100%',
            width: '100%',
            position: 'absolute',
            display: 'block',
        },
    })
);

type CharacterHeroSX = {
    heroCharInfoName: SxProps<Theme>;
    heroCharInfoMainInfo: SxProps<Theme>;
    heroCharInfoRankInfo: SxProps<Theme>;
    heroCharInfoDivider: SxProps<Theme>;
    heroCharInfoErrorText: SxProps<Theme>;
}

const SXElements: CharacterHeroSX = {
    heroCharInfoName: {
        fontSize: '5rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '4rem',
        },
    },
    heroCharInfoMainInfo: {
        fontSize: '1.5rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.2rem',
        },
    },
    heroCharInfoRankInfo: {
        fontSize: '1rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.8rem',
        },
    },
    heroCharInfoDivider: {
        backgroundColor: "rgba(255,255,255,0.6)",
    },
    heroCharInfoErrorText: {
        display: 'inline-block',
        textShadow: '1px 1px 5px #000',
        color: "#FFF",
        top: '40%',
        position: 'absolute',
        textAlign: 'center',
        width: '100%',
        [theme.breakpoints.down('lg')]: {
            position: 'absolute',
            top: '40%',
            padding: '0 20px',
            left: 0,
        },
        [theme.breakpoints.down('xs')]: {
            top: '20%',
        },
    }
}

interface CharacterRouteMatch {
    region: string;
    name: string;
}

interface CharacterHeroProps {
    hasLoaded: boolean;
    charData: APICharacterData | undefined;
}

const CharacterHero: FC<CharacterHeroProps> = (props: CharacterHeroProps) => {
    const classes = useStyles();
    const matches = useRouteMatch<CharacterRouteMatch>();

    const fullRegionTitle = (): string => {
        if (props.charData !== undefined) {
            if (matches.params.region.toLowerCase() === "gms") {
                return "| MapleStory Global";
            } else if (matches.params.region.toLowerCase() === "gms") {
                return "| MapleStory Europe";
            }
        }

        return ""
    }

    const classServerRank = (): string => {
        if (props.charData !== undefined && props.charData.ServerClassRanking !== -1) {
            return `| ${props.charData.Class} Rank in ${props.charData.Server} ${props.charData.ServerClassRanking}`
        }
        return ""
    }

    if (!props.hasLoaded) {
        return (
            <Fragment>
                <div className={classes.container}>
                    <div className={classes.heroImage}></div>
                    <div className={classes.heroInfo}>
                    </div>
                </div>
            </Fragment>
        );
    }

    if (props.hasLoaded && props.charData !== undefined) {
        return (
            <Fragment>
                <div className={classes.container}>
                    <div className={classes.heroImage}></div>
                    <div className={classes.heroInfo}>
                        <div className={classes.heroCharImage}>
                            <div className={classes.heroCharImageOffset}>
                                <CharacterHeroImage charAvatar={props.charData.CharacterImageURL} charClass={props.charData.Class} charName={props.charData.Name} />
                            </div>
                        </div>
                        <div className={classes.heroCharInfo}>
                            <div className={classes.heroCharInfoText}>
                                <Typography variant="h2" sx={SXElements.heroCharInfoName}>{props.charData.Name}</Typography>
                                <Typography variant="subtitle1" sx={SXElements.heroCharInfoMainInfo}>Level {props.charData.Level} ({props.charData.EXPPercent}%) | {props.charData.Class} | {props.charData.Server} {fullRegionTitle()}</Typography>
                                <Divider sx={SXElements.heroCharInfoDivider} />
                                <Typography variant="subtitle1" sx={SXElements.heroCharInfoRankInfo}>Global Rank {props.charData.GlobalRanking} | {props.charData.Class} Rank {props.charData.ClassRank} | {props.charData.Server} Rank {props.charData.ServerRank} {classServerRank()}</Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <div className={classes.container}>
                <div className={classes.heroImage}></div>
                <div className={classes.heroInfo}>
                    <div className={classes.heroInfoError}>
                        <Typography sx={SXElements.heroCharInfoErrorText} variant="h2">Unable to find character</Typography>
                    </div>
                </div>
            </div>
        </Fragment>
    );

}

export default CharacterHero;
